import React, {FC, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {makeGetIsTeamConversation} from '../../../../../../libs/inboxes/store/src/conversations/conversationsSelectors';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {blendColors} from '../../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {PopoutLocationsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {makeGetDefaultCellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {
  DetailedActivityConversationLinkedModel,
  LinkedConversationActivityRolesEnum,
} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {unlinkConversationAsyncAction} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {popoutConversationViewerAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {
  makeGetContentPage,
  makeGetViewerPanel,
} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {ConversationPreviewContainer} from '../../../../common/conversations/conversationPreviewContainer';
import {ConversationCellContainer} from '../../../cells/conversationCellContainer';

/*
 * Props.
 */

interface ConversationSummaryCommentUnfurlViewerInheritableProps {
  panelId: ViewerPanelId;
  onRequestClose: () => void;
  isCompact?: boolean;
  numLinkedConversations?: number;
}

interface ConversationSummaryCommentUnfurlConversationViewerProps
  extends ConversationSummaryCommentUnfurlViewerInheritableProps {
  conversation: ConversationModel;
  activity?: never;
}
interface ConversationSummaryCommentUnfurlActivityViewerProps
  extends ConversationSummaryCommentUnfurlViewerInheritableProps {
  conversation?: never;
  activity: DetailedActivityConversationLinkedModel;
}

type ConversationSummaryCommentUnfurlViewerProps =
  | ConversationSummaryCommentUnfurlConversationViewerProps
  | ConversationSummaryCommentUnfurlActivityViewerProps;

/*
 * Style.
 */

const StyledCellHoverIconsWrapper = styled.div`
  display: none;

  padding: 2px;
  z-index: 2;
  border-radius: 8px;
  background: ${(p) => blendColors(p.theme.greys.shade20, p.theme.alphas.gray10)};

  position: absolute;
  right: 5px;
  top: 5px;
  max-width: 58px;
  flex-direction: row-reverse;
`;

const StyledUnfurlButton = styled(ChromelessButton)`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  &:hover {
    background-color: ${(p) => blendColors(p.theme.greys.shade20, p.theme.alphas.gray10)};

    ${StyledCellHoverIconsWrapper} {
      display: flex;
    }
  }
  // We disable pointer events here so the button will be the one controlling the hover/clicks and so that any actions from the cell won't work.
  & > * {
    &:not(${StyledCellHoverIconsWrapper}) {
      pointer-events: none;
    }
  }
`;

const StyledOverlayDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  padding: 7px 8px 8px 8px;

  border-radius: 8px;
  background-color: ${(p) => p.theme.alphas.white50};

  transition: opacity 300ms ease;
  opacity: 0;

  ${StyledUnfurlButton}:hover & {
    opacity: 1;
  }
`;

const StyledDetailsDiv = styled.div`
  display: grid;
  grid-template-areas: 'text icon';
  grid-template-columns: 1fr auto;
  grid-column-gap: 4px;
  align-items: center;
`;

const StyledTextDiv = styled.div`
  grid-area: text;
  padding-left: 4px;
  font-weight: ${fontWeights.semibold};
`;

const StyledRightIconDiv = styled.div`
  grid-area: icon;
  padding: 2px;
  margin-right: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};
`;

const StyledIconDiv = styled.div`
  height: 100%;
  ${center()};
`;

/*
 * Component.
 */

export const ConversationSummaryCommentUnfurlViewer: FC<ConversationSummaryCommentUnfurlViewerProps> = (
  props,
) => {
  const theme = useUserTheme();
  const {activity, conversation, panelId} = props;

  const dispatch = useAppDispatch();
  const {linkedConversation, activityId} = useGetUnfurlState(activity, conversation);
  const getCellsContext = useMemo(
    () => makeGetDefaultCellsContext(undefined, makeGetIsTeamConversation(getLinkedConversation)),
    [],
  );
  const cellsContext = useAppSelector((state) => getCellsContext(state, {linkedConversation}));
  const {trackDataEvent} = useDataEventTracking();
  const conversationRef = useRef<HTMLDivElement>(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);

  const viewerPageParameters = useGetViewerPageParameters(props.panelId);

  if (!linkedConversation) {
    return null;
  }

  const onConversationSingleClick = async () => {
    dispatch(
      popoutConversationViewerAction(
        linkedConversation.id,
        activityId,
        undefined,
        viewerPageParameters,
        PopoutLocationsEnum.CONVERSATION_SUMMARY,
      ),
    );

    props.onRequestClose();

    if (activity) {
      trackDataEvent(
        DataEventActionsEnum.CLICK,
        LinkedConversationsDataEventObject.VIEW_LINKED_CONVERSATION,
        {
          sourceType:
            activity.linkedConversationRole === LinkedConversationActivityRolesEnum.LINKED
              ? LinkedConversationActivityRolesEnum.ORIGINAL
              : LinkedConversationActivityRolesEnum.LINKED,
          sourceConversationId: activity.conversationId,
          targetConversationId: activity.linkedConversationId,
        },
      );
    }
  };

  const onUnlinkClick = async (event: FrontClickEvent) => {
    event.preventDefault();
    if (activity) {
      dispatch(unlinkConversationAsyncAction(activity));

      trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.UNLINK_CONVERSATION, {
        sourceType:
          activity.linkedConversationRole === LinkedConversationActivityRolesEnum.LINKED
            ? LinkedConversationActivityRolesEnum.ORIGINAL
            : LinkedConversationActivityRolesEnum.LINKED,
        sourceConversationId: activity.conversationId,
        targetConversationId: activity.linkedConversationId,
      });
    }
    // Only close if we're not in conversation summary and there's only 1 conversation
    if (props.isCompact && props.numLinkedConversations === 1) {
      props.onRequestClose();
    }
  };

  const unlinkLabel =
    linkedConversation.linkStatus === LinkedConversationStatusesEnum.ORIGINAL ? (
      <Trans id="conversation-summary-unlink-conversation">Unlink</Trans>
    ) : (
      <Trans id="conversation-summary-unlink-original-conversation">Unlink from original</Trans>
    );

  return (
    <StyledUnfurlButton
      onClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onConversationSingleClick();
      }}
      onMouseEnter={() => setShouldShowPreview(true)}
      onMouseLeave={() => setShouldShowPreview(false)}
      ref={conversationRef}
    >
      <ConversationCellContainer
        panelId={props.panelId}
        conversation={linkedConversation}
        cellsContext={cellsContext}
        isExtraConversation={false}
        parameters={undefined}
        isTransparentPreview
        areHoverActionsEnabled
      />
      <StyledOverlayDiv>
        <StyledDetailsDiv>
          <StyledTextDiv>
            <Trans id="comment-unfurl-link-view-in-new-window">View in new window</Trans>
          </StyledTextDiv>
          <StyledRightIconDiv>
            <Icon name="externalLinkTiny" color={theme.greys.white} />
          </StyledRightIconDiv>
        </StyledDetailsDiv>
      </StyledOverlayDiv>
      {activity && (
        <StyledCellHoverIconsWrapper>
          <StyledIconDiv>
            <IconButton
              iconName="unlinkedConversationsSmall"
              onClick={onUnlinkClick}
              label={unlinkLabel}
              labelMargin={2}
              size={VisualSizesEnum.SMALL}
              color={theme.greys.shade70}
              hoverColor={theme.greys.shade80}
            />
          </StyledIconDiv>
        </StyledCellHoverIconsWrapper>
      )}
      {shouldShowPreview && (
        <ConversationPreviewContainer
          panelId={panelId}
          conversation={linkedConversation}
          anchor={conversationRef}
          viewerConversationId={undefined}
          shouldRenderSummaryIfAvailable
        />
      )}
    </StyledUnfurlButton>
  );
};

/*
 * Helpers.
 */

interface UnfurlState {
  linkedConversation: ConversationModel | undefined;
  activityId: number | undefined;
}

function useGetUnfurlState(
  activity?: DetailedActivityConversationLinkedModel,
  conversation?: ConversationModel,
): UnfurlState {
  const conversations = useAppSelector(getConversations);

  const maybeGetConversationFromActivity = () => {
    if (!activity?.linkedConversationId) {
      return undefined;
    }
    return conversations[activity.linkedConversationId];
  };
  const linkedConversation = conversation ?? maybeGetConversationFromActivity();
  const activityId = activity?.id;

  return {linkedConversation, activityId};
}

function useGetViewerPageParameters(panelId: ViewerPanelId) {
  const getContentPage = useMemo(() => {
    const getViewerPanel = makeGetViewerPanel<void>(() => panelId);

    return makeGetContentPage(getViewerPanel);
  }, [panelId]);

  const viewerPage = useAppSelector(getContentPage);

  return viewerPage.parameters;
}

/*
 * Selectors.
 */

const getLinkedConversation = (state: unknown, params: {linkedConversation: ConversationModel | undefined}) =>
  params.linkedConversation;
